import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const LoadingWheel = ({ isLoading = false, loadingText = "Scanner en cours..." }) => {
  const [progress, setProgress] = useState(0);
  
  // Progression cyclique uniquement quand isLoading est true
  useEffect(() => {
    let interval;
    if (isLoading) {
      interval = setInterval(() => {
        setProgress(prev => (prev >= 100 ? 0 : prev + 1));
      }, 100);
    }
    
    return () => clearInterval(interval);
  }, [isLoading]);

  // Configuration des couleurs professionnelles
  const primaryColor = "#2271b3";    // Bleu professionnel
  const secondaryColor = "#21a6de";  // Bleu ciel technique
  const accentColor = "#64b5f6";     // Bleu clair
  const darkColor = "#2c3e50";       // Fond sombre
  const brightAccent = "#0dcaf0";    // Accent lumineux pour points
  
  return (
    <div className="d-flex flex-column align-items-center justify-content-center my-2" style={{ height: '220px' }}>
      {/* Conteneur principal */}
      <div className="position-relative d-flex align-items-center justify-content-center" style={{ height: '180px', width: '180px' }}>
        
        {/* Effet de fond avec pulsation AMPLIFIÉE */}
        <motion.div 
          className="position-absolute rounded-circle" 
          style={{ 
            width: '160px',
            height: '160px',
            background: `radial-gradient(circle, rgba(33, 166, 222, 0.25), rgba(44, 62, 80, 0.05))`
          }}
          animate={{ 
            scale: isLoading ? [0.9, 1.3, 0.9] : 1,
            opacity: isLoading ? [0.2, 0.5, 0.2] : 0.2
          }}
          transition={{ 
            repeat: Infinity, 
            duration: 3,
            ease: "easeInOut"
          }}
        />
        
        {/* Cercle externe avec rotation AMPLIFIÉE */}
        <motion.div
          className="position-absolute rounded-circle"
          style={{
            width: '180px',
            height: '180px',
            background: `linear-gradient(135deg, ${darkColor}, ${primaryColor}, ${darkColor})`,
            opacity: 0.15,
            filter: 'blur(8px)'
          }}
          animate={{
            rotate: isLoading ? 360 : 0,
            scale: isLoading ? [0.9, 1.15, 0.9] : 1
          }}
          transition={{ 
            rotate: { duration: 8, repeat: Infinity, ease: "linear" },
            scale: { duration: 4, repeat: Infinity, ease: "easeInOut" }
          }}
        />
        
        {/* Cercle de base avec léger effet 3D */}
        <div 
          className="position-absolute rounded-circle" 
          style={{ 
            width: '150px',
            height: '150px',
            backgroundColor: '#1a222e',
            border: `1px solid #334155`,
            boxShadow: 'inset 0 5px 15px rgba(0,0,0,0.6), 0 2px 5px rgba(33,114,179,0.07)'
          }}
        />
        
        {/* Cercle de progression avec plusieurs couches - MOUVEMENTS AMPLIFIÉS */}
        <svg className="position-absolute" style={{ width: '180px', height: '180px' }} viewBox="0 0 100 100">
          {/* Grille d'arrière-plan avec animation plus visible */}
          <motion.path
            d="M20,50 L80,50 M50,20 L50,80 M26,26 L74,74 M26,74 L74,26"
            fill="none"
            strokeWidth="0.4"
            stroke="rgba(33, 166, 222, 0.25)"
            strokeDasharray="1,3"
            animate={{ opacity: isLoading ? [0.1, 0.5, 0.1] : 0.1 }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          />
          
          {/* Cercle externe technique avec rotation RAPIDE */}
          <motion.circle
            cx="50"
            cy="50"
            r="43"
            fill="none"
            strokeWidth="0.7"
            stroke={primaryColor}
            strokeLinecap="round"
            strokeDasharray="2,5"
            animate={{ 
              rotate: isLoading ? 720 : 0
            }}
            transition={{ 
              rotate: { duration: 12, repeat: Infinity, ease: "linear" }
            }}
            style={{
              transformOrigin: "center",
              opacity: 0.6
            }}
          />
          
          {/* Cercle primaire rotatif AMPLIFIÉ */}
          <motion.circle
            cx="50"
            cy="50"
            r="40"
            fill="none"
            strokeWidth="3"
            stroke="url(#blue-pro-gradient)"
            strokeLinecap="round"
            animate={{ 
              pathLength: isLoading ? [0.05, 0.9, 0.05] : 0.5,
              rotate: isLoading ? 360 : 0 
            }}
            transition={{ 
              pathLength: { duration: 5, repeat: Infinity, ease: "easeInOut" },
              rotate: { duration: 7, repeat: Infinity, ease: "linear" }
            }}
            style={{
              strokeDasharray: "251",
              transformOrigin: "center",
              filter: "drop-shadow(0 0 3px rgba(33, 114, 179, 0.8))"
            }}
          />
          
          {/* Cercle secondaire rotatif (sens inverse) AMPLIFIÉ */}
          <motion.circle
            cx="50"
            cy="50"
            r="32"
            fill="none"
            strokeWidth="1.5"
            stroke="url(#blue-accent-gradient)"
            strokeLinecap="round"
            strokeDasharray="3,5"
            animate={{ 
              pathLength: isLoading ? [0.1, 1, 0.1] : 0.6,
              rotate: isLoading ? -720 : 0 
            }}
            transition={{ 
              pathLength: { duration: 4, repeat: Infinity, ease: "easeInOut" },
              rotate: { duration: 10, repeat: Infinity, ease: "linear" }
            }}
            style={{
              transformOrigin: "center",
              opacity: 0.8
            }}
          />
          
          {/* Segments techniques PLUS VISIBLES */}
          <motion.path
            d="M50,5 L50,20 M50,80 L50,95 M5,50 L20,50 M80,50 L95,50"
            fill="none"
            strokeWidth="1.5"
            stroke={accentColor}
            animate={{ 
              opacity: isLoading ? [0.3, 0.9, 0.3] : 0.3,
              scale: isLoading ? [0.95, 1.05, 0.95] : 1
            }}
            transition={{ 
              opacity: { duration: 3, repeat: Infinity, ease: "easeInOut" },
              scale: { duration: 4, repeat: Infinity, ease: "easeInOut" }
            }}
            style={{
              transformOrigin: "center",
              opacity: 0.6
            }}
          />
          
          {/* Définition des gradients PLUS VIFS */}
          <defs>
            <linearGradient id="blue-pro-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor={primaryColor} />
              <stop offset="50%" stopColor={secondaryColor} />
              <stop offset="100%" stopColor={primaryColor} />
            </linearGradient>
            <linearGradient id="blue-accent-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor={secondaryColor} />
              <stop offset="50%" stopColor={accentColor} />
              <stop offset="100%" stopColor={secondaryColor} />
            </linearGradient>
          </defs>
        </svg>
        
        {/* DEUX effets de balayage "scanner" pour plus de dynamisme */}
        <motion.div 
          className="position-absolute"
          style={{
            height: '120%',
            width: '15px',
            background: `linear-gradient(to bottom, transparent, ${secondaryColor}88, transparent)`,
            opacity: isLoading ? 0.8 : 0,
            filter: 'blur(5px)',
            left: '50%',
            top: '-10%',
            transform: 'translateX(-50%)',
            transformOrigin: 'center'
          }}
          animate={{ 
            rotate: isLoading ? 360 : 0,
          }}
          transition={{ 
            rotate: { repeat: Infinity, duration: 1.8, ease: "linear" },
          }}
        />
        
        <motion.div 
          className="position-absolute"
          style={{
            height: '120%',
            width: '12px',
            background: `linear-gradient(to bottom, transparent, ${brightAccent}66, transparent)`,
            opacity: isLoading ? 0.7 : 0,
            filter: 'blur(5px)',
            left: '50%',
            top: '-10%',
            transform: 'translateX(-50%)',
            transformOrigin: 'center'
          }}
          animate={{ 
            rotate: isLoading ? -360 : 0,
          }}
          transition={{ 
            rotate: { repeat: Infinity, duration: 2.3, ease: "linear" },
          }}
        />
        
        {/* Points de repère - AMPLIFIÉS */}
        {[0, 90, 180, 270].map((angle, index) => (
          <motion.div 
            key={`point-${index}`}
            className="position-absolute"
            style={{
              width: index % 2 === 0 ? '6px' : '5px',
              height: index % 2 === 0 ? '6px' : '5px',
              top: '50%',
              left: '50%',
              marginLeft: index % 2 === 0 ? '-3px' : '-2.5px',
              marginTop: index % 2 === 0 ? '-3px' : '-2.5px',
              borderRadius: index % 2 === 0 ? '50%' : '0',
              backgroundColor: index % 2 === 0 ? primaryColor : secondaryColor,
              transform: `rotate(${angle}deg) translate(0, -45px)`,
              opacity: 0.8
            }}
            animate={{ 
              opacity: isLoading ? [0.5, 1, 0.5] : 0.5,
              scale: isLoading ? [1, 1.5, 1] : 1
            }}
            transition={{ 
              repeat: Infinity, 
              duration: 1.5, 
              ease: "easeInOut",
              delay: index * 0.2
            }}
          />
        ))}
        
        {/* Points lumineux externes - AMPLIFIÉS */}
        {[45, 135, 225, 315].map((angle, index) => (
          <motion.div
            key={`glow-point-${index}`}
            className="position-absolute"
            style={{
              width: '12px',
              height: '12px',
              top: '50%',
              left: '50%',
              marginLeft: '-6px',
              marginTop: '-6px',
              borderRadius: '50%',
              backgroundColor: brightAccent,
              boxShadow: `0 0 12px 4px ${brightAccent}`,
              transform: `rotate(${angle}deg) translate(0, -70px)`
            }}
            animate={{ 
              scale: isLoading ? [0.8, 2, 0.8] : 1,
              opacity: isLoading ? [0.4, 0.9, 0.4] : 0.3
            }}
            transition={{ 
              scale: { repeat: Infinity, duration: 2 + (index * 0.1), ease: "easeInOut" },
              opacity: { repeat: Infinity, duration: 2 + (index * 0.2), ease: "easeInOut" }
            }}
          />
        ))}
        
        {/* Points lumineux SUPPLÉMENTAIRES pour plus de mouvement */}
        {[22.5, 67.5, 112.5, 157.5, 202.5, 247.5, 292.5, 337.5].map((angle, index) => (
          <motion.div
            key={`extra-glow-point-${index}`}
            className="position-absolute"
            style={{
              width: '8px',
              height: '8px',
              top: '50%',
              left: '50%',
              marginLeft: '-4px',
              marginTop: '-4px',
              borderRadius: '50%',
              backgroundColor: index % 2 === 0 ? brightAccent : secondaryColor,
              boxShadow: `0 0 8px 2px ${index % 2 === 0 ? brightAccent : secondaryColor}`,
              transform: `rotate(${angle}deg) translate(0, -55px)`
            }}
            animate={{ 
              scale: isLoading ? [0.7, 1.6, 0.7] : 1,
              opacity: isLoading ? [0.3, 0.8, 0.3] : 0.2
            }}
            transition={{ 
              scale: { repeat: Infinity, duration: 3 - (index * 0.1), ease: "easeInOut" },
              opacity: { repeat: Infinity, duration: 2.5 - (index * 0.1), ease: "easeInOut" }
            }}
          />
        ))}
        
        {/* Orbite externe avec pulsation */}
        <motion.div
          className="position-absolute rounded-circle"
          style={{
            width: '140px',
            height: '140px',
            top: '50%',
            left: '50%',
            marginLeft: '-70px',
            marginTop: '-70px',
            border: `1.5px dashed ${secondaryColor}44`,
            opacity: 0.4
          }}
          animate={{
            rotate: isLoading ? 360 : 0,
            scale: isLoading ? [0.95, 1.05, 0.95] : 1
          }}
          transition={{
            rotate: { repeat: Infinity, duration: 15, ease: "linear" },
            scale: { repeat: Infinity, duration: 3, ease: "easeInOut" }
          }}
        />
        
        {/* Centre technologique AMPLIFIÉ */}
        <motion.div
          className="position-absolute rounded-circle d-flex align-items-center justify-content-center"
          style={{
            width: '70px',
            height: '70px',
            background: 'radial-gradient(circle, rgba(26, 34, 46, 0.95), rgba(26, 34, 46, 0.85))',
            boxShadow: `inset 0 0 15px rgba(0, 0, 0, 0.4), 0 0 4px ${primaryColor}88`,
            border: `1px solid ${darkColor}`
          }}
          animate={{
            scale: isLoading ? [0.98, 1.04, 0.98] : 1
          }}
          transition={{
            scale: { repeat: Infinity, duration: 2, ease: "easeInOut" }
          }}
        >
          {/* Orbe centrale */}
          <motion.div
            className="rounded-circle d-flex align-items-center justify-content-center"
            style={{
              width: '54px',
              height: '54px',
              background: `radial-gradient(circle, ${darkColor}dd, ${darkColor}aa)`,
              boxShadow: `inset 0 0 8px rgba(0, 0, 0, 0.6), 0 0 5px ${primaryColor}66`
            }}
            animate={{ 
              scale: isLoading ? [0.95, 1.05, 0.95] : 1
            }}
            transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
          >
            {/* Compteur dynamique AMPLIFIÉ */}
            <motion.div
              className="text-center fw-bold"
              style={{ 
                fontSize: '18px',
                color: secondaryColor,
                fontFamily: 'Consolas, monospace',
                textShadow: `0 0 5px ${secondaryColor}77`
              }}
              animate={{ 
                opacity: isLoading ? [0.7, 1, 0.7] : 0.5,
                scale: isLoading ? [0.9, 1.1, 0.9] : 1
              }}
              transition={{ 
                repeat: Infinity, 
                duration: 1, 
                ease: "easeInOut" 
              }}
            >
              {isLoading ? `${progress}%` : ''}
            </motion.div>
          </motion.div>
          
          {/* Lignes de scan multiples avec mouvement AMPLIFIÉ */}
          {[1, 2].map((idx) => (
            <motion.div
              key={`scan-line-${idx}`}
              className="position-absolute"
              style={{
                width: '100%',
                height: '2px',
                background: `linear-gradient(to right, transparent, ${brightAccent}, transparent)`,
                opacity: isLoading ? 0.8 : 0,
                filter: 'blur(1px)'
              }}
              animate={{ 
                top: isLoading ? 
                  idx === 1 ? ['15%', '85%', '15%'] : ['85%', '15%', '85%'] : '50%'
              }}
              transition={{ 
                repeat: Infinity, 
                duration: idx === 1 ? 1.5 : 2, 
                ease: "easeInOut"
              }}
            />
          ))}
          
          {/* Lignes rotatives supplémentaires */}
          <motion.div
            className="position-absolute rounded-circle"
            style={{
              width: '60px',
              height: '60px',
              border: `1px dashed ${secondaryColor}44`,
              opacity: 0.5
            }}
            animate={{
              rotate: isLoading ? 360 : 0
            }}
            transition={{
              rotate: { repeat: Infinity, duration: 5, ease: "linear" }
            }}
          />
        </motion.div>
      </div>
      
      {/* Texte d'indication avec effet de typing AMPLIFIÉ */}
      <motion.div 
        className="mt-3 fw-medium"
        style={{ 
          minHeight: '24px',
          color: secondaryColor,
          fontSize: '15px',
          letterSpacing: '0.8px',
          fontFamily: 'Arial, sans-serif',
          textShadow: `0 0 3px ${secondaryColor}44`
        }}
        animate={{ 
          opacity: isLoading ? [0.7, 1, 0.7] : 0.5
        }}
        transition={{ repeat: Infinity, duration: 1.2 }}
      >
        {isLoading ? (
          <motion.span
            animate={{ opacity: [1, 1, 1, 0, 1] }}
            transition={{ repeat: Infinity, duration: 2, times: [0, 0.8, 0.9, 0.95, 1] }}
          >
            {loadingText}
          </motion.span>
        ) : (
          <span>Prêt</span>
        )}
      </motion.div>
      
      {/* Indicateur de processus AMPLIFIÉ */}
      {isLoading && (
        <motion.div
          className="d-flex gap-3 mt-2"
          animate={{ opacity: [0.7, 1, 0.7] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
        >
          {['Analyse', 'Détection', 'Optimisation'].map((step, idx) => (
            <motion.div 
              key={`status-${idx}`}
              className="small"
              style={{ 
                fontSize: '12px',
                color: idx === Math.floor(progress / 34) ? brightAccent : '#6c757d',
                fontFamily: 'Arial, sans-serif',
                fontWeight: idx === Math.floor(progress / 34) ? 'bold' : 'normal'
              }}
              animate={{
                scale: idx === Math.floor(progress / 34) ? [1, 1.1, 1] : 1
              }}
              transition={{
                scale: { repeat: Infinity, duration: 1, ease: "easeInOut" }
              }}
            >
              {step}
              <span className="ms-1">
                {idx < Math.floor(progress / 34) ? '✓' : '•'}
              </span>
            </motion.div>
          ))}
        </motion.div>
      )}
    </div>
  );
};

export default LoadingWheel;